/**
 * One Idempotency-Key per user intent (kitchen flow pilot fix A3).
 *
 * On weak wifi a tap can reach the server, commit, and lose its response
 * (axios gives up after 30s). If the retry carries a NEW key the server sees a
 * second, real action and moves stock twice. So a key belongs to an intent —
 * "approve REQ-…-004 as it stands now", "waste 2 kg chicken, spoiled" — and is
 * reused for every retry of that intent. It is renewed only after a success,
 * or when the scope changes (another document, a newer version of it, or
 * different inputs), because that is a different intent.
 *
 * Scope strings are built by the caller: put in everything that makes it a
 * different action (document id + action + updated_at, line id, the form's
 * inputs). The server replays the stored answer for a repeated key and stores
 * nothing on a refusal, so reusing a key after a 4xx is safe too.
 */
import { useCallback, useRef, useState } from 'react'
import { newIdempotencyKey } from './offlineQueue'

export class IntentKeys {
  private keys = new Map<string, string>()

  constructor(private mint: () => string = newIdempotencyKey) {}

  /** The key for this intent: the same one until renew(scope). */
  keyFor(scope: string): string {
    let k = this.keys.get(scope)
    if (!k) {
      k = this.mint()
      this.keys.set(scope, k)
    }
    return k
  }

  /** The intent went through: the next one with this scope is a new action. */
  renew(scope: string): void {
    this.keys.delete(scope)
  }
}

/** Several intents in one component (e.g. one per line): keyFor(scope) / renew(scope). */
export function useIntentKeys(): IntentKeys {
  const ref = useRef<IntentKeys | null>(null)
  if (!ref.current) ref.current = new IntentKeys()
  return ref.current
}

/**
 * One intent: `key` stays the same for this scope until renew() (call it on
 * success). A new scope gets a new key.
 */
export function useIntentKey(scope: string): { key: string; renew: () => void } {
  const keys = useIntentKeys()
  const [, bump] = useState(0)
  const renew = useCallback(() => {
    keys.renew(scope)
    bump((n) => n + 1)
  }, [keys, scope])
  return { key: keys.keyFor(scope), renew }
}

/** A stable text for a form's inputs, for use in a scope. */
export function intentScope(...parts: unknown[]): string {
  return parts.map((p) => (typeof p === 'string' ? p : JSON.stringify(p ?? null))).join('|')
}
